import React, { useState } from "react";
import { X, Loader2, Mail, User, Building2, FileText, MapPin, Plus, Trash2, Landmark, Home, Building, Shield } from "lucide-react";
import { addPartner } from "../api/partners";

// US States list for dropdown
const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "Washington DC" },
];

// Partner types per backend spec
const PARTNER_TYPES = [
  {
    value: "lending",
    label: "Lending Partner",
    description: "Loan officers, mortgage brokers - receives lead emails",
    icon: Landmark,
    receivesEmails: true,
  },
  {
    value: "realtor",
    label: "Realtor Partner",
    description: "Real estate agents",
    icon: Home,
    receivesEmails: false,
    badge: "Coming Soon",
  },
  {
    value: "property_manager",
    label: "Property Manager",
    description: "Property managers",
    icon: Building,
    receivesEmails: false,
    badge: "Coming Soon",
  },
  {
    value: "insurance_agent",
    label: "Insurance Agent",
    description: "Home insurance agents",
    icon: Shield,
    receivesEmails: false,
  },
];

const AddPartnerModal = ({ isOpen, onClose, onPartnerAdded }) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    company: "",
    phone: "",
    partner_type: "lending",
    notes: "",
  });
  const [coverageAreas, setCoverageAreas] = useState([{ city: "", state: "" }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleCoverageChange = (index, field, value) => {
    setCoverageAreas(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addCoverageArea = () => {
    setCoverageAreas(prev => [...prev, { city: "", state: "" }]);
  };

  const removeCoverageArea = (index) => {
    if (coverageAreas.length > 1) {
      setCoverageAreas(prev => prev.filter((_, i) => i !== index));
    }
  };

  const selectedType = PARTNER_TYPES.find(t => t.value === formData.partner_type);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setError("Email is required.");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    // Validate coverage areas for realtor type
    if (formData.partner_type === "realtor") {
      const validCoverageAreas = coverageAreas.filter(area => area.state);
      if (validCoverageAreas.length === 0) {
        setError("Please add at least one coverage area with a state.");
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Filter out empty coverage areas and format
      const validCoverageAreas = coverageAreas
        .filter(area => area.state)
        .map(area => ({
          city: area.city.trim() || null,
          state: area.state,
        }));

      const payload = {
        email: formData.email.trim(),
        name: formData.name.trim() || null,
        company: formData.company.trim() || null,
        phone: formData.phone.trim() || null,
        partner_type: formData.partner_type,
        notes: formData.notes.trim() || null,
      };

      // Only include coverage_areas for realtor type
      if (formData.partner_type === "realtor" && validCoverageAreas.length > 0) {
        payload.coverage_areas = validCoverageAreas;
      }

      const response = await addPartner(payload);
      const newPartner = response?.data || response;

      // Reset form
      setFormData({ email: "", name: "", company: "", phone: "", partner_type: "lending", notes: "" });
      setCoverageAreas([{ city: "", state: "" }]);

      // Notify parent with the new partner (with defaults for display)
      onPartnerAdded({
        email: newPartner.email || newPartner.partner_email || formData.email.trim(),
        name: newPartner.name || formData.name.trim() || null,
        company: newPartner.company || formData.company.trim() || null,
        phone: newPartner.phone || formData.phone.trim() || null,
        partner_type: newPartner.partner_type || formData.partner_type,
        coverage_areas: newPartner.coverage_areas || (formData.partner_type === "realtor" ? validCoverageAreas : null),
        notes: newPartner.notes || formData.notes.trim() || null,
        status: newPartner.status || "active",
        receives_notifications: newPartner.receives_notifications ?? (formData.partner_type === "lending"),
        leadsAccessed: newPartner.leadsAccessed || newPartner.stats?.leads_accessed || 0,
        leadsConverted: newPartner.leadsConverted || newPartner.stats?.leads_converted || 0,
        createdAt: newPartner.createdAt || newPartner.created_at || new Date().toISOString(),
      });
    } catch (err) {
      console.error("Failed to add partner:", err);
      setError(err.message || "Failed to add partner. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ email: "", name: "", company: "", phone: "", partner_type: "lending", notes: "" });
      setCoverageAreas([{ city: "", state: "" }]);
      setError(null);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 dark:bg-slate-900/80 bg-slate-500/50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="rounded-2xl max-w-lg w-full shadow-2xl border animate-scale-in overflow-hidden dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-800 border-slate-200 flex-shrink-0">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Partners
            </p>
            <h3 className="text-xl font-bold dark:text-white text-slate-900">
              Add New Partner
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 rounded-full transition-colors text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
          {error && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          {/* Partner Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-500 mb-2">
              Partner Type <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {PARTNER_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = formData.partner_type === type.value;
                return (
                  <label
                    key={type.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? "border-sky-500 bg-sky-50 dark:bg-sky-900/20 dark:border-sky-500"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                    }`}
                  >
                    <input
                      type="radio"
                      name="partner_type"
                      value={type.value}
                      checked={isSelected}
                      onChange={(e) => handleInputChange("partner_type", e.target.value)}
                      disabled={isSubmitting}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${isSelected ? "text-sky-600 dark:text-sky-400" : "text-slate-500"}`} />
                        <span className={`font-medium ${isSelected ? "text-sky-700 dark:text-sky-300" : "text-slate-700 dark:text-slate-300"}`}>
                          {type.label}
                        </span>
                        {type.badge && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded">
                            {type.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {type.description}
                        {!type.receivesEmails && type.badge && (
                          <span className="text-amber-600 dark:text-amber-400"> - does NOT receive lead emails yet</span>
                        )}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Email Field - Required */}
          <div>
            <label
              htmlFor="partner-email"
              className="block text-sm font-semibold text-slate-500 mb-1"
            >
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                id="partner-email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="partner@example.com"
                required
                disabled={isSubmitting}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Name Field */}
          <div>
            <label
              htmlFor="partner-name"
              className="block text-sm font-semibold text-slate-500 mb-1"
            >
              Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                id="partner-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="John Smith"
                disabled={isSubmitting}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Company Field */}
          <div>
            <label
              htmlFor="partner-company"
              className="block text-sm font-semibold text-slate-500 mb-1"
            >
              Company / Brokerage
            </label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                id="partner-company"
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange("company", e.target.value)}
                placeholder={formData.partner_type === "realtor" ? "Acme Realty" : "ABC Mortgage"}
                disabled={isSubmitting}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Phone Field */}
          <div>
            <label
              htmlFor="partner-phone"
              className="block text-sm font-semibold text-slate-500 mb-1"
            >
              Phone Number
            </label>
            <div className="relative">
              <input
                id="partner-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                placeholder="(555) 123-4567"
                disabled={isSubmitting}
                className="w-full px-4 py-2.5 rounded-lg focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Coverage Areas - Only show for Realtor type */}
          {formData.partner_type === "realtor" && (
            <div>
              <label className="block text-sm font-semibold text-slate-500 mb-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Coverage Areas <span className="text-red-500">*</span>
                </div>
                <span className="text-xs font-normal text-slate-400 mt-0.5 block">
                  Cities and states where this realtor operates
                </span>
              </label>
              <div className="space-y-3">
                {coverageAreas.map((area, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={area.city}
                        onChange={(e) => handleCoverageChange(index, "city", e.target.value)}
                        placeholder="City (optional)"
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 disabled:opacity-50"
                      />
                    </div>
                    <div className="w-32">
                      <select
                        value={area.state}
                        onChange={(e) => handleCoverageChange(index, "state", e.target.value)}
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 disabled:opacity-50"
                      >
                        <option value="">State *</option>
                        {US_STATES.map(state => (
                          <option key={state.code} value={state.code}>{state.code}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCoverageArea(index)}
                      disabled={isSubmitting || coverageAreas.length === 1}
                      className="p-2 text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addCoverageArea}
                  disabled={isSubmitting}
                  className="flex items-center gap-1.5 text-sm text-sky-600 dark:text-sky-400 hover:text-sky-500 font-medium disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Area
                </button>
              </div>
            </div>
          )}

          {/* Notes Field */}
          <div>
            <label
              htmlFor="partner-notes"
              className="block text-sm font-semibold text-slate-500 mb-1"
            >
              Notes
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <textarea
                id="partner-notes"
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Additional notes about this partner..."
                rows={3}
                disabled={isSubmitting}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 disabled:opacity-50 resize-none"
              />
            </div>
          </div>

          {/* Info banner for non-lending partners */}
          {selectedType && !selectedType.receivesEmails && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <span className="text-amber-500 text-lg">⚠️</span>
              <div className="text-sm text-amber-700 dark:text-amber-400">
                <p className="font-medium">Lead emails coming soon</p>
                <p className="text-xs mt-0.5 opacity-80">
                  {selectedType.label}s don't receive lead notification emails yet. This feature is coming soon.
                </p>
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t dark:border-slate-800 border-slate-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg border transition-colors dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-700 dark:hover:bg-slate-800 hover:bg-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? "Adding..." : "Add Partner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPartnerModal;
